/**
 * Property helpers for Grid AI automation.
 * Additive only — omit a field and existing defaults remain unchanged.
 */

export const NAMED_COLORS = {
  red: '#FF0000',
  blue: '#0000FF',
  green: '#00FF00',
  yellow: '#FFFF00',
  orange: '#FFA500',
  purple: '#800080',
  violet: '#8A2BE2',
  indigo: '#4B0082',
  pink: '#FFC0CB',
  cyan: '#00FFFF',
  magenta: '#FF00FF',
  white: '#FFFFFF',
  black: '#000000',
  gray: '#808080',
  grey: '#808080',
  brown: '#A52A2A',
  gold: '#FFD700',
  silver: '#C0C0C0',
  navy: '#000080',
  teal: '#008080',
  lime: '#00FF00',
  maroon: '#800000',
  olive: '#808000',
  aqua: '#00FFFF',
  skyblue: '#87CEEB',
  lightblue: '#ADD8E6',
  darkblue: '#00008B',
  darkred: '#8B0000',
  darkgreen: '#006400',
  darkpurple: '#4B0082',
  lightpurple: '#DDA0DD',
  darkorange: '#FF8C00',
  lightgreen: '#90EE90',
  lightpink: '#FFB6C1',
  lightgray: '#D3D3D3',
  lightgrey: '#D3D3D3',
  darkgray: '#A9A9A9',
  darkgrey: '#A9A9A9',
  darkcyan: '#008B8B',
  darkmagenta: '#8B008B',
  darkviolet: '#9400D3',
  deeppink: '#FF1493',
  hotpink: '#FF69B4',
};

/** Whitelist of create/modify fields forwarded from AI tool params. */
export const CREATE_PROP_KEYS = [
  'color',
  'position',
  'rotation',
  'scale',
  'quantity',
  'particleCount',
  'size',
  'opacity',
  'range',
  'height',
  'intensity',
  'windStrength',
  'turbulence',
  'life',
  'shape',
  'shadow',
  'angle',
  'width',
  'content',
  'fontSize',
  'textType',
  'fontFamily',
  'fontWeight',
  'buttonText',
  'messageText',
  'buttonColor',
  'messageBgColor',
  'extrusion',
  'relativeTo',
  'placement',
  'layout',
  'layoutAxis',
  'spacing',
  'layoutCols',
];

export const PARTICLE_TYPES = [
  'rain',
  'snow',
  'fire',
  'stars',
  'clouds',
  'water',
  'foliage',
];

/** Effects included when the user asks for "all particles" / "all effects". */
export const ADDABLE_PARTICLE_TYPES = [...PARTICLE_TYPES];

export const LIGHT_TYPES = [
  'light',
  'pointlight',
  'directionallight',
  'spotlight',
  'arealight',
  'ambientlight',
];

export const SHAPE_TYPES = [
  'cube',
  'sphere',
  'cylinder',
  'cone',
  'pyramid',
  'capsule',
  'torus',
  'rectangle',
  'ellipse',
  'triangle',
  'plane',
];

export const CONTENT_TYPES = ['text', 'hotspot'];

/**
 * Sanitize free-form text for text meshes / hotspot labels.
 */
export function sanitizeTextContent(input, fallback = 'Spatial Grid') {
  if (input == null) return fallback;
  const raw = String(input).trim();
  if (!raw) return fallback;
  // Strip wrapping quotes the model sometimes includes
  const unquoted = raw.replace(/^["'`]|["'`]$/g, '').trim();
  return (unquoted || fallback).slice(0, 500);
}

function clampByte(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function hexToRgb(hex) {
  const h = String(hex).replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((v) => clampByte(v).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/** Darken a #RRGGBB color toward black (amount 0–1). */
export function darkenHex(hex, amount = 0.4) {
  const { r, g, b } = hexToRgb(hex);
  const t = 1 - Math.max(0, Math.min(1, amount));
  return rgbToHex(r * t, g * t, b * t);
}

/** Lighten a #RRGGBB color toward white (amount 0–1). */
export function lightenHex(hex, amount = 0.4) {
  const { r, g, b } = hexToRgb(hex);
  const a = Math.max(0, Math.min(1, amount));
  return rgbToHex(r + (255 - r) * a, g + (255 - g) * a, b + (255 - b) * a);
}

function isResolvedHexColor(value) {
  return typeof value === 'string' && /^#[0-9A-F]{6}$/i.test(value.trim());
}

/**
 * Normalize color names / short hex into #RRGGBB when possible.
 * Handles "dark purple", "light blue", CSS names, and hex.
 */
export function normalizeColor(input) {
  if (input == null || input === '') return undefined;

  if (typeof input === 'number' && Number.isFinite(input)) {
    const hex = Math.max(0, Math.min(0xffffff, Math.floor(input)))
      .toString(16)
      .padStart(6, '0');
    return `#${hex.toUpperCase()}`;
  }

  const raw = String(input).trim();
  if (!raw) return undefined;

  const lower = raw.toLowerCase().trim();
  const compact = lower.replace(/[\s_-]+/g, '');

  if (NAMED_COLORS[compact]) return NAMED_COLORS[compact];
  if (NAMED_COLORS[lower]) return NAMED_COLORS[lower];

  // Modifier + base: "dark purple", "light blue", "deep red"
  const modMatch = lower.match(
    /^(dark|light|deep|bright|pale|hot)\s+([a-z]+)$/
  );
  if (modMatch) {
    const [, mod, base] = modMatch;
    const compound = NAMED_COLORS[`${mod}${base}`];
    if (compound) return compound;

    const baseHex = NAMED_COLORS[base];
    if (baseHex) {
      if (mod === 'dark' || mod === 'deep') return darkenHex(baseHex, 0.45);
      if (mod === 'light' || mod === 'pale') return lightenHex(baseHex, 0.45);
      if (mod === 'bright') return lightenHex(baseHex, 0.25);
      if (mod === 'hot' && base === 'pink') return NAMED_COLORS.hotpink;
    }
  }

  // #RGB → #RRGGBB
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  if (/^#[0-9a-f]{6}$/i.test(raw)) {
    return raw.toUpperCase();
  }

  // 0xRRGGBB or RRGGBB
  if (/^0x[0-9a-f]{6}$/i.test(raw)) {
    return `#${raw.slice(2).toUpperCase()}`;
  }
  if (/^[0-9a-f]{6}$/i.test(raw)) {
    return `#${raw.toUpperCase()}`;
  }

  // Leave css-like strings (rgb(...)) for Three.js Color parser
  return raw;
}

/**
 * Infer a #RRGGBB color from a natural-language command.
 * Prefers "dark purple" over bare "purple".
 */
export function inferColorFromCommand(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return undefined;

  // 1) Modifier + color phrase
  const modPhrase = text.match(
    /\b(dark|light|deep|bright|pale|hot)\s+([a-z]+)\b/
  );
  if (modPhrase) {
    const candidate = normalizeColor(`${modPhrase[1]} ${modPhrase[2]}`);
    if (isResolvedHexColor(candidate)) return candidate;
  }

  // 2) Longest named color first (darkblue before blue)
  const keys = Object.keys(NAMED_COLORS).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const spaced = key.replace(/^(dark|light|sky|deep|hot)/, '$1 ');
    const patterns = [
      new RegExp(`\\b${key}\\b`, 'i'),
      spaced !== key ? new RegExp(`\\b${spaced.trim()}\\b`, 'i') : null,
    ].filter(Boolean);

    if (patterns.some((re) => re.test(text))) {
      return NAMED_COLORS[key];
    }
  }

  return undefined;
}

/**
 * Infer rotation in degrees from phrases like:
 * "rotated 90° on X axis", "rotate 45 degrees around y".
 * Returns { x?, y?, z? } in degrees, or null.
 */
export function inferRotationFromCommand(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return null;

  const rotation = {};
  const apply = (axis, value) => {
    if (!axis || !Number.isFinite(value)) return;
    rotation[String(axis).toLowerCase()] = value;
  };

  const patterns = [
    /rotat(?:ed|e|ion)?\s*[:=]?\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)?\s*(?:on|around|about|along|in)?\s*(?:the\s+)?([xyz])(?:[\s-]?axis)?/i,
    /(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)\s*(?:on|around|about|along)?\s*(?:the\s+)?([xyz])(?:[\s-]?axis)?/i,
    /\b([xyz])(?:[\s-]?axis)?\s*(?:rotation|rotated|rot)\s*[:=]?\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:grees?)?)?/i,
    /\b(?:rotation|rotated|rot)\s+[:=]?\s*([xyz])\s*[:=]?\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:grees?)?)?/i,
  ];

  for (let i = 0; i < patterns.length; i++) {
    const m = text.match(patterns[i]);
    if (!m) continue;
    if (i <= 1) apply(m[2], parseFloat(m[1]));
    else apply(m[1], parseFloat(m[2]));
    break;
  }

  const multi = [
    ...text.matchAll(
      /\b([xyz])\s*[:=]\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)?/gi
    ),
  ];
  multi.forEach((m) => apply(m[1], parseFloat(m[2])));

  return Object.keys(rotation).length ? rotation : null;
}

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function pickFromSource(source, out) {
  if (!isPlainObject(source)) return;

  for (const key of CREATE_PROP_KEYS) {
    if (source[key] === undefined || source[key] === null) continue;
    if (out[key] !== undefined) continue;
    out[key] = source[key];
  }

  // Flat aliases AI sometimes emits
  if (out.particleCount === undefined) {
    const count =
      source.count ?? source.particle_count ?? source.numParticles;
    if (count !== undefined && count !== null) out.particleCount = count;
  }
  if (out.windStrength === undefined && source.wind != null) {
    if (typeof source.wind === 'number') out.windStrength = source.wind;
    else if (isPlainObject(source.wind) && source.wind.x != null) {
      out.windStrength = Math.abs(Number(source.wind.x)) || undefined;
    }
  }
}

/**
 * Merge nested `properties` + top-level tool params into a whitelist-only object.
 * Normalizes color when present. Does not invent defaults.
 */
export function pickCreateProps(parameters) {
  const raw = isPlainObject(parameters) ? parameters : {};
  const nested = isPlainObject(raw.properties) ? raw.properties : {};
  const out = {};

  // Nested first, then top-level overrides (top-level wins)
  pickFromSource(nested, out);
  const topOnly = { ...raw };
  delete topOnly.properties;
  delete topOnly.objectType;
  delete topOnly.name;
  delete topOnly.type;
  delete topOnly.query;
  pickFromSource(topOnly, out);
  // Re-apply top-level explicitly so it wins over nested for same keys
  for (const key of CREATE_PROP_KEYS) {
    if (topOnly[key] !== undefined && topOnly[key] !== null) {
      out[key] = topOnly[key];
    }
  }

  if (out.color !== undefined) {
    const normalized = normalizeColor(out.color);
    if (normalized !== undefined) out.color = normalized;
    else delete out.color;
  }

  if (out.particleCount !== undefined) {
    const n = Number(out.particleCount);
    if (Number.isFinite(n) && n > 0) {
      out.particleCount = Math.min(Math.floor(n), 50000);
    } else {
      delete out.particleCount;
    }
  }

  if (out.intensity !== undefined) {
    const n = Number(out.intensity);
    if (Number.isFinite(n)) out.intensity = n;
    else delete out.intensity;
  }

  for (const numKey of [
    'size',
    'opacity',
    'range',
    'height',
    'windStrength',
    'turbulence',
    'life',
    'angle',
    'width',
    'fontSize',
    'extrusion',
  ]) {
    if (out[numKey] === undefined) continue;
    const n = Number(out[numKey]);
    if (Number.isFinite(n)) out[numKey] = n;
    else delete out[numKey];
  }

  if (out.quantity !== undefined) {
    const n = Number(out.quantity);
    if (Number.isFinite(n) && n >= 1) {
      out.quantity = Math.min(Math.floor(n), 100);
    } else {
      delete out.quantity;
    }
  }

  return out;
}

/**
 * Detect color / intensity / density / text-content hints from free-text and inject
 * into tool calls when the model omitted them. Never overwrites an existing value.
 */
export function enrichToolCallsFromCommand(userCommand, toolCalls) {
  if (!Array.isArray(toolCalls) || toolCalls.length === 0) return toolCalls;

  const original = String(userCommand || '');
  const text = original.toLowerCase();
  if (!text.trim()) return toolCalls;

  const inferredColor = inferColorFromCommand(original);
  const inferredRotation = inferRotationFromCommand(original);

  let inferredIntensity;
  if (/\b(dim|dimmed|soft|low)\b/.test(text)) inferredIntensity = 0.35;
  else if (/\b(bright|strong|intense|high)\b/.test(text))
    inferredIntensity = 2.5;

  let inferredParticleCount;
  if (/\b(heavy|dense|lots of|many)\b/.test(text))
    inferredParticleCount = 2500;
  else if (/\b(light|sparse|few|gentle)\b/.test(text))
    inferredParticleCount = 400;

  let inferredParticleSize;
  if (/\b(big|large|huge)\b/.test(text)) inferredParticleSize = 0.25;
  else if (/\b(small|tiny)\b/.test(text)) inferredParticleSize = 0.05;

  let inferredFontSize;
  if (/\b(huge|giant|massive)\b/.test(text)) inferredFontSize = 160;
  else if (/\b(big|large)\b/.test(text)) inferredFontSize = 120;
  else if (/\b(small|tiny)\b/.test(text)) inferredFontSize = 40;

  // Quoted content: add text "Hello World" / saying 'Welcome'
  const quoted =
    original.match(/["']([^"']{1,500})["']/) ||
    original.match(
      /\b(?:saying|that says|reading|with text|labelled|labeled)\s+["']?([^"'.,!]{1,200})["']?/i
    );
  let inferredContent = quoted
    ? sanitizeTextContent(quoted[1], '')
    : undefined;

  // Unquoted: "add blue text Hello World" / "add 3d text Sale"
  if (!inferredContent && /\btext\b/.test(text)) {
    const afterText = original.match(/\btext\b(?:\s+(?:2d|3d))?\s+(.+)$/i);
    if (afterText) {
      const noise = new Set([
        'a',
        'an',
        'the',
        'to',
        'scene',
        'here',
        'please',
        '2d',
        '3d',
        ...Object.keys(NAMED_COLORS).map((k) => k.toLowerCase()),
        'large',
        'big',
        'huge',
        'small',
        'tiny',
        'giant',
        'massive',
      ]);
      const words = afterText[1]
        .trim()
        .split(/\s+/)
        .filter((w) => {
          const clean = w.toLowerCase().replace(/[^a-z0-9]/gi, '');
          return clean && !noise.has(clean);
        });
      if (words.length) {
        inferredContent = sanitizeTextContent(words.join(' '), '');
      }
    }
  }

  // Hotspot label from "add hotspot Info" when not quoted
  let inferredHotspotLabel;
  if (/\bhot\s*-?\s*spot\b/.test(text) && !inferredContent) {
    const afterHs = original.match(/\bhot\s*-?\s*spot\b\s+(.+)$/i);
    if (afterHs) {
      const rest = afterHs[1]
        .replace(/\b(?:with\s+)?(?:message|saying|popup)\s+.+$/i, '')
        .replace(/\b(?:labelled|labeled|named|called|label)\s+/i, '')
        .trim();
      const skip = new Set([
        'a',
        'an',
        'the',
        'to',
        'scene',
        'here',
        'please',
        'circle',
        'rectangle',
        ...Object.keys(NAMED_COLORS).map((k) => k.toLowerCase()),
      ]);
      const words = rest
        .split(/\s+/)
        .filter((w) => !skip.has(w.toLowerCase()));
      if (words.length) {
        inferredHotspotLabel = sanitizeTextContent(words.join(' '), '');
      }
    }
  }

  const hotspotMsgMatch = original.match(
    /\b(?:with\s+)?(?:message|saying|popup)\s+["']?([^"'.,!]{1,300})["']?/i
  );
  const inferredHotspotMessage = hotspotMsgMatch
    ? sanitizeTextContent(hotspotMsgMatch[1], '')
    : undefined;

  return toolCalls.map((call) => {
    if (!call || typeof call !== 'object') return call;

    const name = call.name;
    if (
      name !== 'add_object' &&
      name !== 'modify_object' &&
      name !== 'add_text' &&
      name !== 'add_hotspot'
    ) {
      return call;
    }

    const params = {
      ...(isPlainObject(call.parameters) ? call.parameters : {}),
    };
    const props = isPlainObject(params.properties)
      ? { ...params.properties }
      : {};

    const hasColor = params.color != null || props.color != null;
    const hasIntensity =
      params.intensity != null || props.intensity != null;
    const hasCount =
      params.particleCount != null || props.particleCount != null;
    const hasSize = params.size != null || props.size != null;
    const hasFontSize =
      params.fontSize != null || props.fontSize != null;
    const hasContent =
      params.content != null ||
      params.text != null ||
      props.content != null;
    const hasButtonText = params.buttonText != null;
    const hasMessageText = params.messageText != null;

    const objectType = String(
      params.objectType || params.type || ''
    ).toLowerCase();
    const isParticle = PARTICLE_TYPES.includes(objectType);
    const isLight = LIGHT_TYPES.includes(objectType);
    const isTextTool =
      name === 'add_text' ||
      objectType === 'text' ||
      (name === 'modify_object' && objectType === 'text');
    const isHotspotTool =
      name === 'add_hotspot' || objectType === 'hotspot';

    // Always coerce color to a real #RRGGBB. AI often sends "dark purple"
    // which Three.js cannot parse — normalize or fall back to inferred.
    if (hasColor) {
      const existing = params.color ?? props.color;
      const normalized = normalizeColor(existing);
      if (isResolvedHexColor(normalized)) {
        params.color = normalized;
      } else if (inferredColor) {
        params.color = inferredColor;
      } else if (normalized) {
        params.color = normalized;
      }
    } else if (inferredColor) {
      params.color = inferredColor;
    }

    // Rotation from "rotated 90° on X axis" — merge into params.rotation
    if (
      inferredRotation &&
      (name === 'add_object' || name === 'modify_object' || name === 'add_text')
    ) {
      const existingRot = isPlainObject(params.rotation)
        ? params.rotation
        : isPlainObject(props.rotation)
          ? props.rotation
          : {};
      params.rotation = {
        x: existingRot.x ?? inferredRotation.x ?? 0,
        y: existingRot.y ?? inferredRotation.y ?? 0,
        z: existingRot.z ?? inferredRotation.z ?? 0,
      };
      // Prefer inferred axis values when existing is missing/zero and command specified them
      for (const axis of ['x', 'y', 'z']) {
        if (inferredRotation[axis] != null) {
          params.rotation[axis] = inferredRotation[axis];
        }
      }
    }

    if (!hasIntensity && inferredIntensity != null && isLight) {
      params.intensity = inferredIntensity;
    }
    if (!hasCount && inferredParticleCount != null && isParticle) {
      params.particleCount = inferredParticleCount;
    }
    if (!hasSize && inferredParticleSize != null && isParticle) {
      params.size = inferredParticleSize;
    }
    if (!hasFontSize && inferredFontSize != null && isTextTool) {
      params.fontSize = inferredFontSize;
    }
    if (!hasContent && inferredContent && isTextTool) {
      params.content = inferredContent;
    }
    if (isHotspotTool) {
      const label = inferredHotspotLabel || inferredContent;
      if (!hasButtonText && label) params.buttonText = label.slice(0, 40);
      if (!hasMessageText && inferredHotspotMessage)
        params.messageText = inferredHotspotMessage;
      else if (!hasMessageText && inferredContent)
        params.messageText = inferredContent;
    }
    // "2d text" / "3d text"
    if (isTextTool && params.textType == null) {
      if (/\b2d\b/.test(text)) params.textType = '2D';
      else if (/\b3d\b/.test(text)) params.textType = '3D';
    }

    // "make all cubes orange" / "paint every sphere red" / plural "make cubes blue"
    if (name === 'modify_object') {
      const type = objectType || String(params.name || '').toLowerCase();
      const pluralHit =
        type &&
        new RegExp(`\\b${type.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}s\\b`, 'i').test(
          original
        );
      const allHit =
        /\b(?:all|every|each|every\s+single)\b/i.test(original) ||
        pluralHit;
      if (
        allHit ||
        params.all === true ||
        params.all === 'true' ||
        Number(params.quantity) === -1
      ) {
        params.all = true;
      }
      // Drop name filters that are just the type/plural — they'd block multi-match
      if (params.all && params.name) {
        const n = String(params.name).toLowerCase().trim();
        if (
          !type ||
          n === type ||
          n === `${type}s` ||
          n === 'all' ||
          n === `all ${type}` ||
          n === `all ${type}s` ||
          n === `every ${type}` ||
          n === `every ${type}s`
        ) {
          delete params.name;
        }
      }
    }

    if (Object.keys(props).length) {
      params.properties = props;
    }

    return { ...call, parameters: params };
  });
}

/**
 * Apply a color to a mesh or group (Capsule / Rectangle wrappers).
 * Also syncs userData.material_settings.albedo so the editor panel stays correct.
 */
export function applyMeshColor(object, color) {
  if (!object || color == null) return;
  const normalized = normalizeColor(color);
  if (!normalized) return;

  // Prefer hex — Three.js may reject names like "dark purple"
  const colorValue = isResolvedHexColor(normalized)
    ? normalized
    : normalizeColor(String(normalized).replace(/\s+/g, '')) || normalized;

  const applyToMaterial = (material) => {
    if (!material) return;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach((mat) => {
      if (!mat || !mat.color || typeof mat.color.set !== 'function') return;
      try {
        mat.color.set(colorValue);
        mat.needsUpdate = true;
      } catch (_) {
        /* ignore invalid color strings */
      }
    });
  };

  if (object.material) {
    applyToMaterial(object.material);
  }

  if (typeof object.traverse === 'function') {
    object.traverse((child) => {
      if (child.isMesh && child.material) {
        applyToMaterial(child.material);
      }
    });
  }

  if (object.userData?.material_settings) {
    object.userData.material_settings.albedo = colorValue;
  }
  if (isResolvedHexColor(colorValue)) {
    object.userData = object.userData || {};
    object.userData.material_settings = {
      ...(object.userData.material_settings || {}),
      albedo: colorValue,
    };
  }
}

export function isParticleType(type) {
  return PARTICLE_TYPES.includes(String(type || '').toLowerCase());
}

export function isLightType(type) {
  return LIGHT_TYPES.includes(String(type || '').toLowerCase());
}

export function isShapeType(type) {
  return SHAPE_TYPES.includes(String(type || '').toLowerCase());
}
